import { ExpenseClaim, ExpenseClaimListSummary } from '@query'
import { formatRecentTime, RecentTime } from '@utils'
import { TFunction } from 'i18next'

export const expenseClaimListTitle = (claim: ExpenseClaim, t: TFunction): string => {
  if (claim.list_title) {
    return claim.list_title
  }
  if (claim.list_title_month) {
    return t(`travelAndExpenses.list.monthExpensesByMonth.${claim.list_title_month}`)
  }
  return claim.number
}

const formatTripExpenseSummary = (summary: ExpenseClaimListSummary, t: TFunction): string => {
  const { trip_count: trips, expense_count: expenses } = summary
  if (trips === 0 && expenses > 0) {
    return t('travelAndExpenses.list.expenseCount', { count: expenses })
  }
  const parts: string[] = []
  if (trips > 0) {
    parts.push(t('travelAndExpenses.list.tripCount', { count: trips }))
  }
  if (expenses > 0) {
    parts.push(t('travelAndExpenses.list.expenseCount', { count: expenses }))
  }
  return parts.join(', ')
}

const formatDetailSummary = (summary: ExpenseClaimListSummary, t: TFunction): string => {
  const parts: string[] = []
  if (summary.leg_count > 0) {
    parts.push(t('travelAndExpenses.list.legCount', { count: summary.leg_count }))
  }
  if (summary.has_partial_per_diem) {
    parts.push(t('travelAndExpenses.list.partialPerDiem'))
  } else if (summary.has_per_diem) {
    parts.push(t('travelAndExpenses.list.perDiem'))
  }
  const showReceipts =
    summary.expense_count > 0 &&
    (summary.leg_count > 0 || summary.has_per_diem || summary.has_partial_per_diem)
  if (showReceipts) {
    parts.push(t('travelAndExpenses.list.receiptCount', { count: summary.expense_count }))
  }
  return parts.join(', ')
}

export const expenseClaimListMeta = (claim: ExpenseClaim, t: TFunction): string => {
  const summary = claim.list_summary
  if (!summary) {
    return ''
  }
  const primary = formatTripExpenseSummary(summary, t)
  const details = formatDetailSummary(summary, t)
  if (primary && details) {
    return `${primary} · ${details}`
  }
  return primary || details
}

const recentWhen = (when: RecentTime, t: TFunction): string => {
  if (when.kind === 'justNow') return t('travelAndExpenses.list.justNow')
  if (when.kind === 'today') return t('travelAndExpenses.list.today')
  return when.text
}

export const expenseClaimStage = (claim: ExpenseClaim, t: TFunction): string => {
  if (claim.status === 'AWAITING_APPROVAL') {
    return t('travelAndExpenses.list.sent', {
      when: recentWhen(formatRecentTime(claim.submitted_at ?? claim.updated_at), t)
    })
  }
  if (claim.status === 'APPROVED') {
    return t('travelAndExpenses.list.approved', {
      when: recentWhen(formatRecentTime(claim.approved_at ?? claim.updated_at), t)
    })
  }
  if (claim.status === 'PAID') {
    return t('travelAndExpenses.list.paid', {
      when: recentWhen(
        formatRecentTime(claim.paid_on ?? claim.updated_at, {
          dateOnly: claim.paid_on !== null
        }),
        t
      )
    })
  }
  return t('travelAndExpenses.list.edited', {
    when: recentWhen(formatRecentTime(claim.updated_at), t)
  })
}
